'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SolutionOverviewProps {
  currentLanguage: string;
  onSectorSelect: (sector: string) => void;
}

const SolutionOverview = ({ currentLanguage, onSectorSelect }: SolutionOverviewProps) => {
  const [activeSector, setActiveSector] = useState<number | null>(null);

  const content = {
    en: {
      title: 'Comprehensive Infrastructure Solutions',
      subtitle: 'Tailored sustainability strategies for critical sectors',
      sectors: [
        {
          id: 'utilities',
          icon: 'BoltIcon',
          name: 'Utilities Management',
          description: 'Smart grid optimization and renewable energy integration',
          features: ['AI-powered load balancing', 'Real-time energy monitoring', 'Carbon footprint tracking', 'Predictive maintenance'],
          color: 'from-blue-500 to-cyan-500'
        },
        {
          id: 'ports',
          icon: 'TruckIcon',
          name: 'Port Sustainability',
          description: 'Maritime emission reduction and green logistics',
          features: ['Ship emission monitoring', 'Port operations optimization', 'Alternative fuel adoption', 'Supply chain decarbonization'],
          color: 'from-teal-500 to-emerald-500'
        },
        {
          id: 'ecosystem',
          icon: 'GlobeAltIcon',
          name: 'Ecosystem Restoration',
          description: 'Desert and post-flood rejuvenation projects',
          features: ['Biodiversity monitoring', 'Soil health assessment', 'Water conservation', 'Carbon sequestration tracking'],
          color: 'from-green-500 to-lime-500'
        },
        {
          id: 'railways',
          icon: 'SignalIcon',
          name: 'Railway Sustainability',
          description: 'International best practices for rail operations',
          features: ['Energy efficiency optimization', 'Emission reduction strategies', 'Infrastructure upgrades', 'Performance benchmarking'],
          color: 'from-purple-500 to-pink-500'
        }
      ]
    },
    hi: {
      title: 'व्यापक बुनियादी ढांचा समाधान',
      subtitle: 'महत्वपूर्ण क्षेत्रों के लिए अनुकूलित स्थिरता रणनीतियां',
      sectors: [
        {
          id: 'utilities',
          icon: 'BoltIcon',
          name: 'उपयोगिता प्रबंधन',
          description: 'स्मार्ट ग्रिड अनुकूलन और नवीकरणीय ऊर्जा एकीकरण',
          features: ['AI-संचालित लोड बैलेंसिंग', 'वास्तविक समय ऊर्जा निगरानी', 'कार्बन फुटप्रिंट ट्रैकिंग', 'भविष्य कहनेवाला रखरखाव'],
          color: 'from-blue-500 to-cyan-500'
        },
        {
          id: 'ports',
          icon: 'TruckIcon',
          name: 'बंदरगाह स्थिरता',
          description: 'समुद्री उत्सर्जन में कमी और हरित लॉजिस्टिक्स',
          features: ['जहाज उत्सर्जन निगरानी', 'बंदरगाह संचालन अनुकूलन', 'वैकल्पिक ईंधन अपनाना', 'आपूर्ति श्रृंखला डीकार्बनाइजेशन'],
          color: 'from-teal-500 to-emerald-500'
        },
        {
          id: 'ecosystem',
          icon: 'GlobeAltIcon',
          name: 'पारिस्थितिकी तंत्र बहाली',
          description: 'रेगिस्तान और बाढ़ के बाद कायाकल्प परियोजनाएं',
          features: ['जैव विविधता निगरानी', 'मिट्टी स्वास्थ्य मूल्यांकन', 'जल संरक्षण', 'कार्बन अलगाव ट्रैकिंग'],
          color: 'from-green-500 to-lime-500'
        },
        {
          id: 'railways',
          icon: 'SignalIcon',
          name: 'रेलवे स्थिरता',
          description: 'रेल संचालन के लिए अंतरराष्ट्रीय सर्वोत्तम प्रथाएं',
          features: ['ऊर्जा दक्षता अनुकूलन', 'उत्सर्जन में कमी की रणनीतियां', 'बुनियादी ढांचा उन्नयन', 'प्रदर्शन बेंचमार्किंग'],
          color: 'from-purple-500 to-pink-500'
        }
      ]
    },
    ar: {
      title: 'حلول البنية التحتية الشاملة',
      subtitle: 'استراتيجيات استدامة مصممة للقطاعات الحيوية',
      sectors: [
        {
          id: 'utilities',
          icon: 'BoltIcon',
          name: 'إدارة المرافق',
          description: 'تحسين الشبكة الذكية ودمج الطاقة المتجددة',
          features: ['موازنة الحمل بالذكاء الاصطناعي', 'مراقبة الطاقة في الوقت الفعلي', 'تتبع البصمة الكربونية', 'الصيانة التنبؤية'],
          color: 'from-blue-500 to-cyan-500'
        },
        {
          id: 'ports',
          icon: 'TruckIcon',
          name: 'استدامة الموانئ',
          description: 'تقليل الانبعاثات البحرية والخدمات اللوجستية الخضراء',
          features: ['مراقبة انبعاثات السفن', 'تحسين عمليات الموانئ', 'اعتماد الوقود البديل', 'إزالة الكربون من سلسلة التوريد'],
          color: 'from-teal-500 to-emerald-500'
        },
        {
          id: 'ecosystem',
          icon: 'GlobeAltIcon',
          name: 'استعادة النظام البيئي',
          description: 'مشاريع تجديد الصحراء وما بعد الفيضانات',
          features: ['مراقبة التنوع البيولوجي', 'تقييم صحة التربة', 'الحفاظ على المياه', 'تتبع عزل الكربون'],
          color: 'from-green-500 to-lime-500'
        },
        {
          id: 'railways',
          icon: 'SignalIcon',
          name: 'استدامة السكك الحديدية',
          description: 'أفضل الممارسات الدولية لعمليات السكك الحديدية',
          features: ['تحسين كفاءة الطاقة', 'استراتيجيات خفض الانبعاثات', 'تحديث البنية التحتية', 'قياس الأداء'],
          color: 'from-purple-500 to-pink-500'
        }
      ]
    }
  };

  const currentContent = content[currentLanguage as keyof typeof content] || content.en;

  const handleSectorClick = (index: number, sectorId: string) => {
    setActiveSector(activeSector === index ? null : index);
    onSectorSelect(sectorId);
  };

  return (
    <section id="solutions" className="py-16 md:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-text-primary">
            {currentContent.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            {currentContent.subtitle}
          </p>
        </div>

        {/* Sectors Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {currentContent.sectors.map((sector, index) => (
            <div
              key={index}
              className={`group bg-card border-2 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 ${
                activeSector === index
                  ? 'border-primary shadow-natural-lg scale-102'
                  : 'border-border hover:border-primary/50 hover:shadow-natural'
              }`}
              onClick={() => handleSectorClick(index, sector.id)}
            >
              {/* Card Header */}
              <div className={`bg-gradient-to-r ${sector.color} p-6`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                      <Icon name={sector.icon as any} size={28} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-xl text-white">
                        {sector.name}
                      </h3>
                      <p className="text-white/90 text-sm mt-1">
                        {sector.description}
                      </p>
                    </div>
                  </div>
                  <Icon 
                    name={activeSector === index ? 'ChevronUpIcon' : 'ChevronDownIcon'} 
                    size={24} 
                    className="text-white transition-transform duration-300"
                  />
                </div>
              </div>

              {/* Expandable Content */}
              <div 
                className={`transition-all duration-300 ease-in-out ${
                  activeSector === index ? 'max-h-96' : 'max-h-0'
                } overflow-hidden`}
              >
                <div className="p-6 space-y-4">
                  <h4 className="font-semibold text-text-primary">Key Features:</h4>
                  <ul className="space-y-2">
                    {sector.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start space-x-3">
                        <Icon name="CheckCircleIcon" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-text-secondary">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionOverview;